/**
 * Black-Scholes option pricing model with robust Implied Volatility solver
 */

// Standard normal CDF
function normCDF(x) {
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741;
  const a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = x < 0 ? -1 : 1;
  const absX = Math.abs(x) / Math.sqrt(2);
  const t = 1.0 / (1.0 + p * absX);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-absX * absX);
  return 0.5 * (1.0 + sign * y);
}

// Standard normal PDF
function normPDF(x) {
  return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
}

// Calculate d1 and d2
function calculateD1(S, K, T, r, sigma) {
  if (sigma <= 0 || T <= 0) return 0;
  return (Math.log(S / K) + (r + sigma * sigma / 2) * T) / (sigma * Math.sqrt(T));
}

function calculateD2(d1, sigma, T) {
  if (sigma <= 0 || T <= 0) return 0;
  return d1 - sigma * Math.sqrt(T);
}

// Black-Scholes Call price
export function blackScholesCall(S, K, T, r, sigma) {
  if (T <= 0) return Math.max(S - K, 0);
  if (sigma <= 0) return Math.max(S - K, 0);
  const d1 = calculateD1(S, K, T, r, sigma);
  const d2 = calculateD2(d1, sigma, T);
  return S * normCDF(d1) - K * Math.exp(-r * T) * normCDF(d2);
}

// Black-Scholes Put price
export function blackScholesPut(S, K, T, r, sigma) {
  if (T <= 0) return Math.max(K - S, 0);
  if (sigma <= 0) return Math.max(K - S, 0);
  const d1 = calculateD1(S, K, T, r, sigma);
  const d2 = calculateD2(d1, sigma, T);
  return K * Math.exp(-r * T) * normCDF(-d2) - S * normCDF(-d1);
}

// Calculate all Greeks for Call
export function calculateCallGreeks(S, K, T, r, sigma) {
  if (T <= 0 || sigma <= 0) {
    return { delta: S > K ? 1 : 0, gamma: 0, vega: 0, theta: 0, rho: 0 };
  }
  const d1 = calculateD1(S, K, T, r, sigma);
  const d2 = calculateD2(d1, sigma, T);
  const nd1 = normPDF(d1);

  // Exact Black-Scholes formulas, scaled to practical trading units:
  // Vega per 1% vol change, Theta per calendar day, Rho per 1% rate change.
  const delta = normCDF(d1);
  let gamma = nd1 / (S * sigma * Math.sqrt(T));
  let vega = S * nd1 * Math.sqrt(T) / 100;


  // حداقل حساسیت برای نمایش و تحلیل
  
  const theta = (-(S * nd1 * sigma) / (2 * Math.sqrt(T)) - r * K * Math.exp(-r * T) * normCDF(d2)) / 365;
  const rho = K * T * Math.exp(-r * T) * normCDF(d2) / 100;

  return { delta, gamma, vega, theta, rho };
}

// Calculate all Greeks for Put
export function calculatePutGreeks(S, K, T, r, sigma) {
  if (T <= 0 || sigma <= 0) {
    return { delta: S < K ? -1 : 0, gamma: 0, vega: 0, theta: 0, rho: 0 };
  }
  const d1 = calculateD1(S, K, T, r, sigma);
  const d2 = calculateD2(d1, sigma, T);
  const nd1 = normPDF(d1);

  const delta = normCDF(d1) - 1;
  const gamma = nd1 / (S * sigma * Math.sqrt(T));
  const vega = S * nd1 * Math.sqrt(T) / 100;
  const theta = (-(S * nd1 * sigma) / (2 * Math.sqrt(T)) + r * K * Math.exp(-r * T) * normCDF(-d2)) / 365;
  const rho = -K * T * Math.exp(-r * T) * normCDF(-d2) / 100;

  return { delta, gamma, vega, theta, rho };
}

// Convenience function to calculate Greeks based on option type
export function calculateGreeks(S, K, T, r, sigma, optionType) {
  if (optionType === 'call') {
    return calculateCallGreeks(S, K, T, r, sigma);
  }
  return calculatePutGreeks(S, K, T, r, sigma);
}

// Black-Scholes price wrapper by option type
export function blackScholesPrice(S, K, T, r, sigma, optionType) {
  if (optionType === 'call') {
    return blackScholesCall(S, K, T, r, sigma);
  }
  return blackScholesPut(S, K, T, r, sigma);
}

// Intrinsic value
export function calculateIntrinsicValue(S, K, optionType) {
  if (optionType === 'call') return Math.max(S - K, 0);
  if (optionType === 'put') return Math.max(K - S, 0);
  return 0;
}

// Time value
export function calculateTimeValue(optionPrice, intrinsicValue) {
  return Math.max(optionPrice - intrinsicValue, 0);
}

// Convert Jalali date to Gregorian (returns [gy, gm, gd] where gm is 0-indexed)
function jalaliToGregorian(jy, jm, jd) {
  jy = parseInt(jy);
  jm = parseInt(jm);
  jd = parseInt(jd);

  let gy = jy <= 979 ? 621 : 1600;
  jy -= jy <= 979 ? 0 : 979;

  let days = (jy <= 979 ? 0 : 365 * 33) +
    365 * jy +
    Math.floor(jy / 33) * 8 +
    Math.floor(((jy % 33) + 3) / 4) +
    78 +
    jd +
    (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);

  gy += 400 * Math.floor(days / 146097);
  days %= 146097;

  if (days > 36524) {
    gy += 100 * Math.floor(--days / 36524);
    days %= 36524;
    if (days >= 365) days++;
  }

  gy += 4 * Math.floor(days / 1461);
  days %= 1461;

  if (days > 365) {
    gy += Math.floor((days - 1) / 365);
    days = (days - 1) % 365;
  }

  let gd = days + 1;
  const gMonthDays = [0, 31, (gy % 4 === 0 && (gy % 100 !== 0 || gy % 400 === 0)) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  let gm = 0;
  for (let i = 1; i <= 12; i++) {
    if (gd <= gMonthDays[i]) {
      gm = i;
      break;
    }
    gd -= gMonthDays[i];
  }

  return [gy, gm - 1, gd];
}

function formatGregorianDate(gy, gm, gd) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${gy}/${pad(gm + 1)}/${pad(gd)}`;
}

// Helper: strip time component from a date (midnight)
function dateAtMidnight(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

// Calculate time to maturity in years from a Jalali or Gregorian date string
export function calculateTimeToMaturity(maturityDate) {
  if (!maturityDate) return 0;

  // Handle ISO format (YYYY-MM-DD)
  if (maturityDate.includes('-')) {
    const parts = maturityDate.split('-');
    if (parts.length === 3) {
      const gy = parseInt(parts[0]);
      const gm = parseInt(parts[1]);
      const gd = parseInt(parts[2]);
      const maturity = new Date(gy, gm - 1, gd);
      const today = dateAtMidnight(new Date());
      const maturityMidnight = dateAtMidnight(maturity);
      const diffMs = maturityMidnight - today;

      if (diffMs <= 0) {
        return 1 / 365;
      }
      return Math.max(diffMs / (1000 * 60 * 60 * 24 * 365), 1 / 365);
    }
    return 0;
  }

  // Handle slash format (YYYY/MM/DD or Jalali)
  const parts = maturityDate.split('/');
  if (parts.length !== 3) return 0;

  const year = parseInt(parts[0]);
  const month = parseInt(parts[1]);
  const day = parseInt(parts[2]);
  let maturity;

  if (year >= 1300 && year <= 1500) {
    const [gy, gm, gd] = jalaliToGregorian(year, month, day);
    maturity = new Date(gy, gm, gd); // gm is already 0-indexed from jalaliToGregorian
  } else {
    maturity = new Date(year, month - 1, day);
  }

  const today = dateAtMidnight(new Date());
  const maturityMidnight = dateAtMidnight(maturity);
  const diffMs = maturityMidnight - today;

  // If the option expires today or the date is in the past, use a minimum one-day maturity
  // so the math can still execute instead of returning 0.
  if (diffMs <= 0) {
    return 1 / 365;
  }

  return Math.max(diffMs / (1000 * 60 * 60 * 24 * 365), 1 / 365);
}

// Robust Implied Volatility solver using Newton-Raphson with bisection fallback
export function calculateImpliedVolatility(S, K, T, r, marketPrice, optionType, initialGuess = 0.40, tolerance = 0.0001, maxIterations = 100) {
  if (T <= 0) return null;
  if (marketPrice <= 0) return null;

  const intrinsicValue = calculateIntrinsicValue(S, K, optionType);
  // Allow a small tolerance for market price slightly below intrinsic due to spread or stale data
  const intrinsicTolerance = 0.01;
  if (marketPrice < intrinsicValue - intrinsicTolerance) {
    return null; // price significantly below intrinsic value - no valid IV
  }

// For deep ITM options:
// Do not force IV to zero.
// Even small time value contains volatility information.

if (marketPrice < intrinsicValue) {

  const difference = intrinsicValue - marketPrice;

  // Allow small market spread error
  if (difference <= intrinsicTolerance) {
    marketPrice = intrinsicValue + 0.01;
  } else {
    return null;
  }
}
  const lowerBound = 0.0001;
  const upperBound = 5.0;

  let sigma = initialGuess;
  let price = blackScholesPrice(S, K, T, r, sigma, optionType);
  let diff = price - marketPrice;

  // Newton-Raphson method
  for (let i = 0; i < maxIterations; i++) {
    // Vega as raw derivative (price per unit sigma, not per 1%)
    const d1 = calculateD1(S, K, T, r, sigma);
    const vegaRaw = S * normPDF(d1) * Math.sqrt(T);

    if (vegaRaw < 1e-12) {
      break; // Vega too small, fall back to bisection
    }

    const nextSigma = sigma - diff / vegaRaw;

    if (nextSigma < lowerBound || nextSigma > upperBound) {
      break; // Out of bounds, fall back to bisection
    }

    sigma = nextSigma;
    price = blackScholesPrice(S, K, T, r, sigma, optionType);
    diff = price - marketPrice;

    if (Math.abs(diff) < tolerance) {
      return sigma;
    }
  }

  // Fallback to bisection method
  let low = lowerBound;
  let high = upperBound;
  let lowPrice = blackScholesPrice(S, K, T, r, low, optionType);
  let highPrice = blackScholesPrice(S, K, T, r, high, optionType);

  // Check if root is bracketed: f(low) and f(high) should have opposite signs
  // For monotonic BS price vs sigma, price increases with sigma
  // If marketPrice is outside [lowPrice, highPrice], the root is not bracketed
  if ((lowPrice - marketPrice) * (highPrice - marketPrice) > 0) {
    // Root not bracketed - marketPrice is outside the range achievable with sigma in [low, high]
    // Return the bound that gives price closest to marketPrice
    if (Math.abs(lowPrice - marketPrice) < Math.abs(highPrice - marketPrice)) {
      return low;
    } else {
      return high;
    }
  }

  // Bisection loop
  for (let i = 0; i < 200; i++) {
    const mid = (low + high) / 2;
    const midPrice = blackScholesPrice(S, K, T, r, mid, optionType);

    // Check which half contains the root
    if ((lowPrice - marketPrice) * (midPrice - marketPrice) <= 0) {
      // Root is in [low, mid]
      high = mid;
      highPrice = midPrice;
    } else {
      // Root is in [mid, high]
      low = mid;
      lowPrice = midPrice;
    }

    if (Math.abs(high - low) < tolerance) {
      return (low + high) / 2;
    }
  }

  return (low + high) / 2;
}

/**
 * Taylor expansion premium prediction - LIVE PRICING APPROACH
 * 
 * این تابع قیمت عادلانه لحظه‌ای (Live Fair Value) را بر اساس فرمول تیلور محاسبه می‌کند.
 * 
 * پارامترها:
 * @param {number} currentPrice - قیمت بسته شدن دیروز (P₀)
 * @param {number} delta - دلتای محاسبه شده از داده‌های دیروز
 * @param {number} gamma - گامای محاسبه شده از داده‌های دیروز
 * @param {number} vega - وگای محاسبه شده از داده‌های دیروز
 * @param {number} theta - تتا (روزانه) - این مقدار منفی است و یک روز کامل افت را اعمال می‌کند
 * @param {number} deltaS - ΔS واقعی = (قیمت لحظه‌ای امروز) - (قیمت بسته شدن دیروز)
 * @param {number} deltaIV - ΔIV = (IV امروز) - (IV دیروز) - معمولاً برای معاملات درون‌روز صفر در نظر گرفته می‌شود
 * 
 * @returns {number} قیمت عادلانه لحظه‌ای (Live Fair Value)
 * 
 * فرمول: P_today = P₀ + Δ×ΔS + ½×Γ×(ΔS)² + Vega×ΔIV + Θ×1 (یک روز)
 */
export function predictOptionPremium(currentPrice, delta, gamma, vega, theta, deltaS, deltaIV) {
  const deltaComponent = delta * deltaS;
  const gammaComponent = 0.5 * gamma * deltaS * deltaS;
  const vegaComponent = vega * deltaIV;

  // Theta به صورت روزانه محاسبه شده است و برای یک روز کامل اعمال می‌شود
  // تتا برای پوزیشن‌های لانگ منفی است (افت ارزش زمانی)
  const thetaComponent = theta; // theta * 1 day

  const fairValue = currentPrice + deltaComponent + gammaComponent + vegaComponent + thetaComponent;
  
  // لاگ برای دیباگ
  console.log(`[DEBUG] predictOptionPremium (Live Pricing):
    P₀ = ${currentPrice}
    ΔS = ${deltaS}
    ΔIV = ${deltaIV}
    Δ×ΔS = ${deltaComponent}
    ½×Γ×(ΔS)² = ${gammaComponent}
    Vega×ΔIV = ${vegaComponent}
    Θ×1 = ${thetaComponent}
    قیمت عادلانه = ${fairValue}
  `);

  return Math.max(fairValue, 0);
}

// Main option calculation function using real market data
export function calculateOptionValueFromAPI(
  symbol,
  underlyingPrice,
  optionPrice,
  strikePrice,
  maturityDate,
  baseSymbol,
  optionType,
  previousPremium = 0,
  previousUnderlyingPrice = 0,
  initialGuess = 0.40,
  riskFreeRate = 0.30,
  actualDeltaS = null // پارامتر جدید برای ΔS واقعی (Live Pricing)
) {
  const timeToMaturity = calculateTimeToMaturity(maturityDate);
  const intrinsicValue = calculateIntrinsicValue(underlyingPrice, strikePrice, optionType);
  const timeValue = calculateTimeValue(optionPrice, intrinsicValue);

  // Convert Jalali maturity date to Gregorian for display
  let maturityDateGregorian = maturityDate;
  if (maturityDate && maturityDate.includes('/')) {
    const parts = maturityDate.split('/');
    if (parts.length === 3) {
      const year = parseInt(parts[0]);
      if (year >= 1300 && year <= 1500) {
        const [gy, gm, gd] = jalaliToGregorian(year, parseInt(parts[1]), parseInt(parts[2]));
        maturityDateGregorian = formatGregorianDate(gy, gm, gd);
      }
    }
  }

  // Solve IV from today's premium - no fallback to a default value
  let ivToday = calculateImpliedVolatility(
    underlyingPrice,
    strikePrice,
    timeToMaturity,
    riskFreeRate,
    optionPrice,
    optionType,
    initialGuess
  );

  // کنترل کیفیت IV برای آپشن‌های Deep ITM
let adjustedIV = ivToday;

const minimumIV = 0.0001;

if (
 adjustedIV !== null &&
 adjustedIV < minimumIV &&
 timeValue > 0
) {
 adjustedIV = minimumIV;
}

const ivSolved = adjustedIV !== null;

  // Solve IV from previous premium (if provided)
  let ivYesterday = null;
  let deltaIV = null;
  if (previousPremium > 0) {
    ivYesterday = calculateImpliedVolatility(
      previousUnderlyingPrice || underlyingPrice, // use previous underlying price if available
      strikePrice,
      timeToMaturity,
      riskFreeRate,
      previousPremium,
      optionType,
      initialGuess
    );
    if (adjustedIV !== null && ivYesterday !== null) {
      deltaIV = adjustedIV - ivYesterday;
    }
  }

    // Greeks computed only using the solved IV; no default 40% fallback
  let greeks = null;
  let fairValue = null;
  let predictedTomorrowPrice = null;
  let samplePredictedPremium = null;
  let finalDeltaS = 0; // ✅ اضافه کنید
  if (ivSolved) {
    greeks = calculateGreeks(
  underlyingPrice,
  strikePrice,
  timeToMaturity,
  riskFreeRate,
  adjustedIV,
  optionType
);

fairValue = blackScholesPrice(
  underlyingPrice,
  strikePrice,
  timeToMaturity,
  riskFreeRate,
  adjustedIV,
  optionType
);

ivToday = adjustedIV;

    // Calculate actual ΔS and ΔIV for prediction
    // اگر ΔS واقعی از ورودی ارسال شده باشد از آن استفاده می‌کنیم، در غیر این صورت از تفاوت قیمت‌ها محاسبه می‌شود
    const calculatedDeltaS = previousUnderlyingPrice > 0 ? underlyingPrice - previousUnderlyingPrice : 0;
    finalDeltaS = actualDeltaS !== null ? actualDeltaS : calculatedDeltaS; // ✅ const حذف شد
    
    console.log(`[DEBUG] calculateOptionValueFromAPI - ΔS استفاده شده: ${finalDeltaS} (${actualDeltaS !== null ? 'واقعی از ورودی' : 'محاسبه شده از تفاوت قیمت‌ها'})`);
    
    const actualDeltaIV = deltaIV !== null ? deltaIV / 100 : 0;

    const basePremium = previousPremium > 0 
    ? previousPremium 
    : optionPrice;


    predictedTomorrowPrice = predictOptionPremium(
    basePremium,
    greeks.delta,
    greeks.gamma,
    greeks.vega,
    greeks.theta,
    finalDeltaS,
    actualDeltaIV
    );

    // Sample prediction with fixed values (for comparison)
    const sampleDeltaS = 16;
    const sampleDeltaIV = 0.02; // 2%
    samplePredictedPremium = predictOptionPremium(optionPrice, greeks.delta, greeks.gamma, greeks.vega, greeks.theta, sampleDeltaS, sampleDeltaIV);
  }

  return {
    isValid: true, // always valid if we have basic data
    ivSolved, // flag to indicate if IV was successfully solved
    symbol,
    optionType,
    baseSymbol,
    strikePrice,
    maturityDate,
    maturityDateGregorian,
    timeToMaturity: timeToMaturity.toFixed(4),
    underlyingPrice,
    optionPrice,
    previousPremium,
    previousUnderlyingPrice,
    intrinsicValue: intrinsicValue.toFixed(2),
    timeValue: timeValue.toFixed(2),
    fairValue: fairValue !== null ? fairValue.toFixed(4) : 'N/A',
    riskFreeRate: riskFreeRate.toFixed(4),
    initialGuess: initialGuess.toFixed(4),
    ivToday: ivToday !== null ? ivToday.toFixed(4) : null,
    ivTodayPercent: ivToday !== null 
  ? Math.max(ivToday * 100, 0.01).toFixed(2) 
  : 'N/A',
    ivYesterday: ivYesterday !== null ? ivYesterday.toFixed(4) : null,
    ivYesterdayPercent: ivYesterday !== null ? (ivYesterday * 100).toFixed(2) : 'N/A',
    deltaIV: deltaIV !== null ? deltaIV.toFixed(4) : 'N/A',
    deltaIVPercent: deltaIV !== null 
    ? (deltaIV * 100).toFixed(2)
    : 'N/A',
    greeks: greeks ? {
      delta: greeks.delta.toFixed(4),
      gamma: greeks.gamma.toFixed(6),
      vega: greeks.vega.toFixed(4),
      theta: greeks.theta.toFixed(4),
      rho: greeks.rho.toFixed(4)
    } : null,
      predictions: {
        actualDeltaS: finalDeltaS,
        actualDeltaIV: deltaIV !== null ? deltaIV : 0,
        actualDeltaIVDecimal: deltaIV !== null ? deltaIV / 100 : 0, // ✅ اصلاح شد
        predictedTomorrowPrice: predictedTomorrowPrice !== null ? predictedTomorrowPrice.toFixed(2) : 'N/A',
        sampleDeltaS: 16,
        sampleDeltaIV: 2,
        samplePredictedPremium: samplePredictedPremium !== null ? samplePredictedPremium.toFixed(2) : 'N/A',
        underlyingPriceToday: underlyingPrice,
        previousUnderlyingPrice: previousUnderlyingPrice,
        deltaSMethod: actualDeltaS !== null ? 'Live (واقعی از ورودی)' : 'Calculated (محاسبه شده از تفاوت قیمت‌ها)'
      }
  };
}

